// Permission bits shared by server and clients. Stored in Postgres as bigint.
export const P = {
  VIEW_CHANNEL: 1n << 0n,
  SEND_MESSAGES: 1n << 1n,
  READ_MESSAGE_HISTORY: 1n << 2n,
  ADD_REACTIONS: 1n << 3n,
  ATTACH_FILES: 1n << 4n,
  MENTION_EVERYONE: 1n << 5n,
  MANAGE_MESSAGES: 1n << 6n,
  MANAGE_CHANNELS: 1n << 7n,
  MANAGE_ROLES: 1n << 8n,
  MANAGE_SERVER: 1n << 9n,
  CREATE_INVITE: 1n << 10n,
  KICK_MEMBERS: 1n << 11n,
  BAN_MEMBERS: 1n << 12n,
  MODERATE_MEMBERS: 1n << 13n,
  CONNECT: 1n << 14n,
  SPEAK: 1n << 15n,
  VIDEO: 1n << 16n,
  STREAM: 1n << 17n,
  VIEW_AUDIT_LOG: 1n << 18n,
  MANAGE_NICKNAMES: 1n << 19n,
  CHANGE_NICKNAME: 1n << 20n,
  MUTE_MEMBERS: 1n << 21n,
  DEAFEN_MEMBERS: 1n << 22n,
  MOVE_MEMBERS: 1n << 23n,
  /** Speak without voice activity detection gating being enforced (reserved; push-to-talk channels). */
  USE_VAD: 1n << 24n,
  ADMINISTRATOR: 1n << 40n,
} as const;

export type PermissionName = keyof typeof P;

export const ALL_PERMISSIONS: bigint = Object.values(P).reduce((a, b) => a | b, 0n);

export const DEFAULT_EVERYONE: bigint =
  P.VIEW_CHANNEL | P.SEND_MESSAGES | P.READ_MESSAGE_HISTORY | P.ADD_REACTIONS | P.ATTACH_FILES |
  P.CREATE_INVITE | P.CONNECT | P.SPEAK | P.VIDEO | P.STREAM | P.CHANGE_NICKNAME | P.USE_VAD;

/** Permissions that only make sense on a channel overwrite. */
export const CHANNEL_SCOPED: bigint =
  P.VIEW_CHANNEL | P.SEND_MESSAGES | P.READ_MESSAGE_HISTORY | P.ADD_REACTIONS | P.ATTACH_FILES |
  P.MENTION_EVERYONE | P.MANAGE_MESSAGES | P.MANAGE_CHANNELS | P.MANAGE_ROLES | P.CREATE_INVITE |
  P.CONNECT | P.SPEAK | P.VIDEO | P.STREAM | P.MUTE_MEMBERS | P.DEAFEN_MEMBERS | P.MOVE_MEMBERS | P.USE_VAD;

/** Fixed permissions inside direct messages (there are no roles). */
export const DM_PERMISSIONS: bigint =
  P.VIEW_CHANNEL | P.SEND_MESSAGES | P.READ_MESSAGE_HISTORY | P.ADD_REACTIONS | P.ATTACH_FILES |
  P.CONNECT | P.SPEAK | P.VIDEO | P.STREAM | P.USE_VAD;

export interface RoleLike { id: string; permissions: bigint; position: number; isEveryone: boolean }
export interface OverwriteLike { targetId: string; targetKind: 0 | 1; allow: bigint; deny: bigint }

export interface MemberContext {
  userId: string;
  isOwner: boolean;
  everyone: RoleLike;
  roles: RoleLike[]; // the member's roles, excluding @everyone
}

export const has = (perms: bigint, bit: bigint): boolean => (perms & bit) === bit;

/** Server-level permissions: @everyone | member roles. Owner and ADMINISTRATOR get everything. */
export function basePermissions(m: MemberContext): bigint {
  if (m.isOwner) return ALL_PERMISSIONS;
  let perms = m.everyone.permissions;
  for (const r of m.roles) perms |= r.permissions;
  if (has(perms, P.ADMINISTRATOR)) return ALL_PERMISSIONS;
  return perms;
}

/**
 * Channel permissions, Discord semantics:
 * base → @everyone overwrite → union of role overwrites (deny then allow) → member overwrite.
 * Without VIEW_CHANNEL nothing else applies. Without SEND_MESSAGES, message-dependent bits are removed.
 */
export function channelPermissions(m: MemberContext, overwrites: OverwriteLike[]): bigint {
  const base = basePermissions(m);
  if (has(base, P.ADMINISTRATOR)) return ALL_PERMISSIONS;
  let perms = base;
  const everyoneOw = overwrites.find((o) => o.targetKind === 0 && o.targetId === m.everyone.id);
  if (everyoneOw) perms = (perms & ~everyoneOw.deny) | everyoneOw.allow;
  const roleIds = new Set(m.roles.map((r) => r.id));
  let allow = 0n, deny = 0n;
  for (const o of overwrites) {
    if (o.targetKind === 0 && roleIds.has(o.targetId)) { allow |= o.allow; deny |= o.deny; }
  }
  perms = (perms & ~deny) | allow;
  const memberOw = overwrites.find((o) => o.targetKind === 1 && o.targetId === m.userId);
  if (memberOw) perms = (perms & ~memberOw.deny) | memberOw.allow;
  if (!has(perms, P.VIEW_CHANNEL)) return 0n;
  if (!has(perms, P.SEND_MESSAGES)) perms &= ~(P.ATTACH_FILES | P.MENTION_EVERYONE);
  return perms;
}

/** Highest role position the member holds (owner = Infinity). */
export function topPosition(m: MemberContext): number {
  if (m.isOwner) return Number.POSITIVE_INFINITY;
  return m.roles.reduce((max, r) => Math.max(max, r.position), 0);
}

/** Can `actor` manage (edit/assign/delete) a role at `position`? Strictly-higher rule. */
export const canManageRolePosition = (actor: MemberContext, position: number): boolean =>
  topPosition(actor) > position;

/** Can `actor` act on `target` (kick/ban/edit roles)? Owner is untouchable. */
export function canActOnMember(actor: MemberContext, target: MemberContext): boolean {
  if (target.isOwner) return false;
  if (actor.userId === target.userId) return false;
  return topPosition(actor) > topPosition(target);
}

export function permissionNames(perms: bigint): PermissionName[] {
  return (Object.keys(P) as PermissionName[]).filter((k) => has(perms, P[k]));
}
